import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { logout, getDolar } from '../api';
import { LogOut, Menu, DollarSign } from 'lucide-react';

const WMO = {
  0:'☀️', 1:'🌤️', 2:'⛅', 3:'☁️', 45:'🌫️', 51:'🌦️',
  61:'🌧️', 63:'🌧️', 65:'🌧️', 80:'🌦️', 95:'⛈️',
};

function useReloj() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export default function Navbar({ onToggleSidebar }) {
  const { user, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [dolar, setDolar] = useState(null);
  const [clima, setClima] = useState(null);
  const now = useReloj();

  const hora       = now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const horaMobile = now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  const fecha      = now.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });

  useEffect(() => {
    getDolar().then(r => setDolar(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetch('https://api.open-meteo.com/v1/forecast?latitude=-34.6037&longitude=-58.3816' +
      '&current=temperature_2m,apparent_temperature,weathercode,windspeed_10m,relativehumidity_2m' +
      '&timezone=America%2FArgentina%2FBuenos_Aires')
      .then(r => r.json())
      .then(d => setClima(d.current))
      .catch(() => {});
  }, []);

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const climaEmoji = clima ? (WMO[clima.weathercode] || '🌡️') : null;
  const climaTemp  = clima ? `${Math.round(clima.temperature_2m)}°C` : null;
  const dolarOk    = dolar && !dolar.error;

  return (
    <nav className="navbar">
      {/* ─── Fila principal ──────────────────────────────────── */}
      <div className="navbar-main">
        <button
          className="nav-hamburger"
          onClick={onToggleSidebar}
          title="Menú"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-2)', padding: '6px', borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Menu size={20} strokeWidth={1.75} />
        </button>

        <NavLink to="/" className="navbar-brand" style={{ flexShrink: 0 }}>
          <img
            src="/logo-rd.png"
            alt="RD Printer"
            style={{ height: 28 }}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
          <span className="navbar-brand-text" style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-2)' }}>
            Portal RD Printer
          </span>
        </NavLink>

        {/* ── Zona central: solo reloj ── */}
        <div className="nav-center-area">
          {/* Desktop */}
          <div className="nav-info-bar">
            <div className="nav-info-item" style={{ flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '.5px' }}>
                {hora}
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>{fecha}</span>
            </div>
          </div>

          {/* Mobile: solo fecha y hora */}
          <div className="nav-clock-mobile">
            <span className="nav-clock-hora">{horaMobile}</span>
            <span className="nav-clock-fecha">{fecha}</span>
          </div>
        </div>

        {/* ── Derecha: dólar + clima + usuario ── */}
        <div className="navbar-right" style={{ flexShrink: 0 }}>
          {/* Dólar — solo desktop */}
          {dolarOk && (
            <div className="nav-dolar-desktop nav-info-item" style={{ gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <DollarSign size={12} strokeWidth={2} color="var(--text-3)" />
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                  Oficial
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 1 }}>Compra</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>${dolar.compra?.toLocaleString('es-AR')}</div>
                </div>
                <div style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 1 }}>Venta</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>${dolar.venta?.toLocaleString('es-AR')}</div>
                </div>
              </div>
            </div>
          )}
          {/* Clima — solo desktop */}
          {climaEmoji && (
            <>
              <div className="nav-sep nav-sep-desktop" />
              <div className="nav-clima-desktop nav-info-item">
                <span style={{ fontSize: 18, lineHeight: 1 }}>{climaEmoji}</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{climaTemp}</span>
              </div>
            </>
          )}
          <div className="nav-sep nav-sep-desktop" />
          <div
            className="user-chip"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/perfil')}
            title="Ver mi perfil"
          >
            {user.picture_url
              ? <img src={user.picture_url} alt={user.name} />
              : <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: 'white',
                }}>
                  {user.name?.[0]?.toUpperCase()}
                </div>
            }
            <span className="user-chip-name">{user.name?.split(' ')[0]}</span>
            <span className={`role-badge ${effectiveRole} user-chip-role`}>{effectiveRole}</span>
          </div>

          <button
            className="btn btn-ghost btn-sm"
            onClick={handleLogout}
            title="Cerrar sesión"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <LogOut size={15} strokeWidth={1.75} />
            <span className="nav-logout-text">Salir</span>
          </button>
        </div>
      </div>

      {/* ─── Barra secundaria (mobile only) ──────────────────── */}
      <div className="navbar-secondary">
        <div className="nav-info-item">
          <DollarSign size={11} strokeWidth={2} color="var(--text-3)" />
          <span style={{ fontSize: 11, color: 'var(--text-2)' }}>
            {dolarOk
              ? <>Compra <strong>${dolar.compra?.toLocaleString('es-AR')}</strong> / Venta <strong>${dolar.venta?.toLocaleString('es-AR')}</strong></>
              : '—'
            }
          </span>
        </div>
        {climaEmoji && (
          <>
            <div className="nav-sep" />
            <div className="nav-info-item">
              <span style={{ fontSize: 14 }}>{climaEmoji}</span>
              <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{climaTemp}</span>
              <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 3 }}>Bs. As.</span>
            </div>
          </>
        )}
      </div>
    </nav>
  );
}
